import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that displays a product's manufacturer article number
 * (custom.hersteller_artikelnummer metafield, shown instead of the raw
 * internal SKU - Andreas' request 2026-09-10: the internal SKU is redundant
 * for customers). The raw SKU span stays in the DOM (hidden) because the
 * Component base class requires the 'sku' ref to exist.
 * This component listens for variant update events and updates the display
 * accordingly. It handles updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer - The container element for the SKU
 * @property {HTMLElement} sku - The (hidden) span element that holds the raw SKU text
 * @property {HTMLElement} [herstellerArtikelnummer] - The span element that displays the manufacturer article number (optional, added 2026-09-09)
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
  requiredRefs = ['skuContainer', 'sku'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the displayed article number.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }

        if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }
        if (resource) {
          const variantSku = resource.sku || '';
          // Keep the (hidden) raw-SKU ref in sync even though it's not shown,
          // since Component requires this ref to exist.
          this.refs.sku.textContent = variantSku;

          // Manufacturer article number (custom.hersteller_artikelnummer metafield),
          // spliced into the variant JSON in snippets/variant-main-picker.liquid.
          const herstellerArtikelnummerRef = this.refs.herstellerArtikelnummer;
          const herstellerArtikelnummer = resource.hersteller_artikelnummer || '';

          if (herstellerArtikelnummerRef) {
            if (herstellerArtikelnummer) {
              this.style.display = 'block';
              herstellerArtikelnummerRef.textContent = `Art.-Nr. Hersteller: ${herstellerArtikelnummer}`;
              herstellerArtikelnummerRef.style.display = '';
            } else {
              this.style.display = 'none';
              herstellerArtikelnummerRef.textContent = '';
              herstellerArtikelnummerRef.style.display = 'none';
            }
          } else {
            // Fallback for older markup without the manufacturer-number span.
            this.style.display = variantSku ? 'block' : 'none';
          }
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-sku] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-sku-component')) {
  customElements.define('product-sku-component', ProductSkuComponent);
}
